import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbAddressBook } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { type ContactsPayload, parseContactsPayload } from "./contactsSchema";

interface ContactsRendererProps {
  block: StreamingBlock;
}

export const ContactsRenderer = (props: ContactsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<ContactsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseContactsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("contacts_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().contacts.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbAddressBook class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("contacts_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().totalResults === 1
                    ? rt.t("contacts_card.results_one")
                    : rt.t("contacts_card.results", { count: String(data().totalResults) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().contacts}>
                  {(contact) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="truncate text-sm text-primary-text">{contact.name}</span>
                        <span class="shrink-0 text-xs text-muted-text">
                          {contact.source === "directory"
                            ? rt.t("contacts_card.source_directory")
                            : contact.source === "personal"
                              ? rt.t("contacts_card.source_personal")
                              : contact.source === "other"
                                ? rt.t("contacts_card.source_other")
                                : contact.source}
                        </span>
                      </div>
                      <p class="mt-0.5 truncate text-xs text-muted-text">{contact.email}</p>
                      <Show when={contact.title || contact.company}>
                        <p class="mt-0.5 truncate text-xs text-muted-text">
                          {[contact.title, contact.company].filter(Boolean).join(" · ")}
                        </p>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
